/**
 * Resolución de menciones en grupos de WhatsApp — CAPA DE COMPATIBILIDAD.
 *
 * Los mensajes NUEVOS se guardan con las @menciones ya resueltas a `@Nombre`:
 * los que llegan (webhook-whatsapp reescribe el body antes de persistir) y los
 * que mandamos (lasMencionesDelEnvio.ts).
 * Este helper sigue siendo necesario SOLO para:
 *   1. Mensajes HISTÓRICOS en DB que se guardaron con `@<userpart>` crudo
 *      antes de que el backend resolviera (no se hace backfill masivo).
 *   2. El formateo visual de la mención (color/peso) vía splitMentions,
 *      que aplica tanto a `@Nombre` ya resuelto como a `@<digits>` viejo.
 *
 * userpart = prefijo del JID del mencionado:
 *   - teléfono real:  "@51999888777"
 *   - LID (privacy):  "@106511497998376"  ← número opaco, NO es un teléfono
 *
 * Resuelve contra participantes que YA escribieron en el grupo (cada
 * wa_message inbound de grupo trae group_participant_name + jid/phone),
 * usando solo data que ya viaja en los mensajes del thread.
 */

interface ParticipantLike {
  group_participant_name?: string | null;
  group_participant_phone?: string | null;
  group_participant_jid?: string | null;
}

/** Extrae el userpart (dígitos antes de @) de un JID o teléfono. */
function userPart(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/@.*/, '').replace(/:.+$/, '').replace(/\D/g, '');
}

/**
 * Construye un mapa userpart → nombre a partir de los mensajes del grupo.
 * Indexa por phone y por jid-userpart (en LID mode son distintos) para
 * maximizar el match contra lo que aparece en el texto del mensaje.
 */
export function buildGroupMentionMap(messages: ParticipantLike[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of messages) {
    const name = m.group_participant_name?.trim();
    if (!name) continue;
    const fromPhone = userPart(m.group_participant_phone);
    const fromJid = userPart(m.group_participant_jid);
    if (fromPhone) map.set(fromPhone, name);
    if (fromJid) map.set(fromJid, name);
  }
  return map;
}

export interface MentionSegment {
  type: 'text' | 'mention';
  content: string;
}

const MENTION_RE = /@(\d{5,})/g;

const escapar = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const LA_EXPRESION = new WeakMap<Map<string, string>, RegExp>();

/** `@<dígitos>` (lo viejo) o `@Nombre` de alguien conocido (lo nuevo). Los nombres largos primero: «@Mauricio Lopez» no es «@Mauricio». */
function laExpresionDeLasMenciones(nombres: Map<string, string>): RegExp {
  const guardada = LA_EXPRESION.get(nombres);
  if (guardada) return guardada;
  const conocidos = [...new Set(nombres.values())].sort((a, b) => b.length - a.length).map(escapar);
  const porNombre = conocidos.length ? `|(?:${conocidos.join('|')})(?=$|[\\s.,;:!?¿¡)\\]}])` : '';
  const expresion = new RegExp(`@(?:(\\d{5,})${porNombre})`, 'giu');
  LA_EXPRESION.set(nombres, expresion);
  return expresion;
}

/**
 * Parte un texto en segmentos texto/mención. Un `@Nombre` de alguien del mapa se
 * resalta como está; cada `@<digits>` se resuelve a `@<nombre>` si está en el
 * mapa. Fallback cuando no se resuelve:
 *   - LID largo (≥15 díg) u opaco → "@usuario" (mejor que el número crudo)
 *   - teléfono plausible (≤14)    → se deja "@<digits>" (al menos es real)
 */
export function splitMentions(text: string, nameByUserpart: Map<string, string>): MentionSegment[] {
  if (!text || !text.includes('@')) return [{ type: 'text', content: text }];
  const out: MentionSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const expresion = laExpresionDeLasMenciones(nameByUserpart);
  expresion.lastIndex = 0;
  while ((m = expresion.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', content: text.slice(last, m.index) });
    const digits = m[1];
    const resolved = digits ? nameByUserpart.get(digits) : undefined;
    let label: string;
    if (!digits) {
      label = m[0]; // ya es «@Nombre»: se resalta tal cual
    } else if (resolved) {
      label = `@${resolved}`;
    } else if (digits.length >= 15) {
      // LID sin nombre conocido — el número opaco no aporta nada al operador.
      label = '@usuario';
    } else {
      label = `@${digits}`;
    }
    out.push({ type: 'mention', content: label });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: 'text', content: text.slice(last) });
  return out;
}

/**
 * Limpia el PREVIEW de la lista de conversaciones (una línea, sin mapa de
 * participantes disponible). Cualquier `@<dígitos>` que quedó sin resolver —
 * porque el webhook no tenía el nombre del participante al guardar — se colapsa a
 * "@miembro" en vez de mostrar un JID crudo tipo "@47377952055320".
 *
 * Arregla los datos HISTÓRICOS ya guardados sin backfill: opera en el render.
 * Solo para el preview: en el hilo, splitMentions con el mapa de participantes
 * sigue resolviendo al nombre real. Si el mapa está disponible, se usa primero.
 */
export function cleanMentionsForPreview(
  text: string | null | undefined,
  nameByUserpart?: Map<string, string>,
): string {
  if (!text || !text.includes('@')) return text ?? '';
  return text.replace(MENTION_RE, (_full, digits: string) => {
    const resolved = nameByUserpart?.get(digits);
    return resolved ? `@${resolved}` : '@miembro';
  });
}
